// Validation utilities for Tagpo
// Shared validation logic for Philippine-focused application

// Philippine phone number validation
// Format: +63-XXX-XXXX-XXXX (mobile) or +63-X-XXX-XXXX (landline)
// Common mobile prefixes: 917, 918, 919, 920, 921, 922, etc.

export const PHONE_REGEX = /^\+63-9\d{2}-\d{4}-\d{4}$/;
export const PHONE_DIGITS_ONLY = /^639\d{9}$/;

/**
 * Format a phone number to Philippine format: +63-9XX-XXX-XXXX
 * Philippine mobile numbers have 10 digits after the country code (total 12 digits)
 * @param {string} value - Raw phone input
 * @returns {string} - Formatted phone number
 */
export function formatPhoneNumber(value) {
    // Remove all non-digit characters
    let digits = value.replace(/\D/g, '');

    // Handle different input scenarios
    if (digits.startsWith('63')) {
        digits = digits.substring(2);
    } else if (digits.startsWith('0')) {
        digits = digits.substring(1);
    }

    // Limit to 10 digits (9XX XXX XXXX)
    digits = digits.substring(0, 10);

    // Build formatted string: +63-9XX-XXX-XXXX
    let formatted = '+63';

    if (digits.length > 0) {
        formatted += '-' + digits.substring(0, 3); // 9XX
    }
    if (digits.length > 3) {
        formatted += '-' + digits.substring(3, 6); // XXX
    }
    if (digits.length > 6) {
        formatted += '-' + digits.substring(6, 10); // XXXX
    }

    return formatted;
}

/**
 * Extract raw digits from formatted phone number
 * @param {string} formattedPhone - Formatted phone like +63-917-1234-5678
 * @returns {string} - Raw digits like 639171234567
 */
export function extractPhoneDigits(formattedPhone) {
    if (!formattedPhone) return '';
    return formattedPhone.replace(/\D/g, '');
}

/**
 * Validate Philippine phone number
 * @param {string} phone - Phone number (can be formatted or raw)
 * @returns {object} - { isValid: boolean, error: string | null }
 */
export function validatePhone(phone) {
    if (!phone || phone.trim() === '' || phone === '+63') {
        return { isValid: false, error: 'Phone number is required' };
    }

    const digits = extractPhoneDigits(phone);

    if (digits.length < 12) {
        return { isValid: false, error: 'Phone number is incomplete' };
    }

    if (!digits.startsWith('639')) {
        return { isValid: false, error: 'Phone number must start with +63-9XX' };
    }

    if (digits.length !== 12) {
        return { isValid: false, error: 'Phone number must be 10 digits after +63' };
    }

    // Validate it's a valid mobile prefix (9XX)
    const mobilePrefix = digits.substring(2, 3);
    if (mobilePrefix !== '9') {
        return { isValid: false, error: 'Must be a valid Philippine mobile number' };
    }

    return { isValid: true, error: null };
}

/**
 * Validate name field
 * @param {string} name - Name to validate
 * @param {number} minLength - Minimum length (default 2)
 * @param {number} maxLength - Maximum length (default 100)
 * @returns {object} - { isValid: boolean, error: string | null }
 */
export function validateName(name, minLength = 2, maxLength = 100) {
    if (!name || name.trim() === '') {
        return { isValid: false, error: 'Name is required' };
    }

    const trimmed = name.trim();

    if (trimmed.length < minLength) {
        return { isValid: false, error: `Name must be at least ${minLength} characters` };
    }

    if (trimmed.length > maxLength) {
        return { isValid: false, error: `Name must be less than ${maxLength} characters` };
    }

    // Check for valid characters (letters, spaces, hyphens, apostrophes)
    const nameRegex = /^[a-zA-ZÀ-ÿ\s\-'\.]+$/;
    if (!nameRegex.test(trimmed)) {
        return { isValid: false, error: 'Name contains invalid characters' };
    }

    return { isValid: true, error: null };
}

/**
 * Validate email field
 * @param {string} email - Email to validate
 * @returns {object} - { isValid: boolean, error: string | null }
 */
export function validateEmail(email) {
    if (!email || email.trim() === '') {
        return { isValid: false, error: 'Email is required' };
    }

    const trimmed = email.trim().toLowerCase();

    // RFC 5322 compliant email regex (simplified)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(trimmed)) {
        return { isValid: false, error: 'Please enter a valid email address' };
    }

    if (trimmed.length > 254) {
        return { isValid: false, error: 'Email address is too long' };
    }

    return { isValid: true, error: null };
}

/**
 * Validate password field
 * @param {string} password - Password to validate
 * @param {object} options - Validation options
 * @returns {object} - { isValid: boolean, error: string | null }
 */
export function validatePassword(password, options = {}) {
    const {
        minLength = 6,
        maxLength = 128,
        requireUppercase = false,
        requireLowercase = false,
        requireNumber = false,
        requireSpecial = false
    } = options;

    if (!password) {
        return { isValid: false, error: 'Password is required' };
    }

    if (password.length < minLength) {
        return { isValid: false, error: `Password must be at least ${minLength} characters` };
    }

    if (password.length > maxLength) {
        return { isValid: false, error: `Password must be less than ${maxLength} characters` };
    }

    if (requireUppercase && !/[A-Z]/.test(password)) {
        return { isValid: false, error: 'Password must contain at least one uppercase letter' };
    }

    if (requireLowercase && !/[a-z]/.test(password)) {
        return { isValid: false, error: 'Password must contain at least one lowercase letter' };
    }

    if (requireNumber && !/\d/.test(password)) {
        return { isValid: false, error: 'Password must contain at least one number' };
    }

    if (requireSpecial && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        return { isValid: false, error: 'Password must contain at least one special character' };
    }

    return { isValid: true, error: null };
}

/**
 * Validate event name
 * @param {string} name - Event name to validate
 * @returns {object} - { isValid: boolean, error: string | null }
 */
export function validateEventName(name) {
    if (!name || name.trim() === '') {
        return { isValid: false, error: 'Event name is required' };
    }

    const trimmed = name.trim();

    if (trimmed.length < 3) {
        return { isValid: false, error: 'Event name must be at least 3 characters' };
    }

    if (trimmed.length > 100) {
        return { isValid: false, error: 'Event name must be less than 100 characters' };
    }

    return { isValid: true, error: null };
}

/**
 * Validate event description
 * @param {string} description - Description to validate
 * @returns {object} - { isValid: boolean, error: string | null }
 */
export function validateEventDescription(description) {
    if (!description || description.trim() === '') {
        return { isValid: false, error: 'Description is required' };
    }

    const trimmed = description.trim();

    if (trimmed.length < 10) {
        return { isValid: false, error: 'Description must be at least 10 characters' };
    }

    if (trimmed.length > 2000) {
        return { isValid: false, error: 'Description must be less than 2000 characters' };
    }

    return { isValid: true, error: null };
}

/**
 * Validate event date/time
 * @param {string} dateTime - ISO date string
 * @returns {object} - { isValid: boolean, error: string | null }
 */
export function validateEventDateTime(dateTime) {
    if (!dateTime) {
        return { isValid: false, error: 'Date and time is required' };
    }

    const date = new Date(dateTime);

    if (isNaN(date.getTime())) {
        return { isValid: false, error: 'Invalid date format' };
    }

    const now = new Date();
    if (date < now) {
        return { isValid: false, error: 'Event date must be in the future' };
    }

    // Check if date is not too far in the future (2 years max)
    const twoYearsFromNow = new Date();
    twoYearsFromNow.setFullYear(twoYearsFromNow.getFullYear() + 2);

    if (date > twoYearsFromNow) {
        return { isValid: false, error: 'Event date cannot be more than 2 years in the future' };
    }

    return { isValid: true, error: null };
}

/**
 * Validate location name
 * @param {string} location - Location name to validate
 * @returns {object} - { isValid: boolean, error: string | null }
 */
export function validateLocation(location) {
    if (!location || location.trim() === '') {
        return { isValid: false, error: 'Location is required' };
    }

    const trimmed = location.trim();

    if (trimmed.length < 3) {
        return { isValid: false, error: 'Location must be at least 3 characters' };
    }

    if (trimmed.length > 200) {
        return { isValid: false, error: 'Location must be less than 200 characters' };
    }

    return { isValid: true, error: null };
}

/**
 * Validate max slots
 * @param {number|string} slots - Number of slots
 * @returns {object} - { isValid: boolean, error: string | null }
 */
export function validateMaxSlots(slots) {
    const num = parseInt(slots, 10);

    if (isNaN(num)) {
        return { isValid: false, error: 'Number of slots is required' };
    }

    if (num < 1) {
        return { isValid: false, error: 'Must have at least 1 slot' };
    }

    if (num > 10000) {
        return { isValid: false, error: 'Maximum 10,000 slots allowed' };
    }

    return { isValid: true, error: null };
}

/**
 * Validate coordinates
 * @param {number|string} lat - Latitude
 * @param {number|string} lng - Longitude
 * @returns {object} - { isValid: boolean, error: string | null }
 */
export function validateCoordinates(lat, lng) {
    // Coordinates are optional
    if (!lat && !lng) {
        return { isValid: true, error: null };
    }

    // If one is provided, both must be provided
    if ((lat && !lng) || (!lat && lng)) {
        return { isValid: false, error: 'Both latitude and longitude must be provided' };
    }

    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);

    if (isNaN(latNum) || isNaN(lngNum)) {
        return { isValid: false, error: 'Coordinates must be valid numbers' };
    }

    if (latNum < -90 || latNum > 90) {
        return { isValid: false, error: 'Latitude must be between -90 and 90' };
    }

    if (lngNum < -180 || lngNum > 180) {
        return { isValid: false, error: 'Longitude must be between -180 and 180' };
    }

    return { isValid: true, error: null };
}

/**
 * Validate YouTube URL
 * @param {string} url - URL to validate
 * @returns {boolean} - true if valid
 */
export const isValidYoutubeUrl = (url) => {
    if (!url) return false;
    // Regex to check for standard YouTube URLs and shortened, including embed
    const regExp = /^(?:https?:\/\/)?(?:www\.)?(?:poster\.youtube\.com\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=)|youtu\.be\/)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[1].length === 11);
};
